"use client";

/**
 * Carousel View
 * Renders schedule items in a paginated carousel, one date group at a time.
 */

import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { formatDateShort } from "@/lib/utils/formatting";
import type { ScheduleItemRow, ScheduleLabelRow } from "../../../../_logic/types/db-types";
import { ScheduleItemDisplay } from "./ScheduleItemDisplay";
import { DateWithWeekday } from "./DateWithWeekday";
import { ItemDeleteButton } from "./ItemDeleteButton";
import { getFirstLabelColor } from "./label-utils";
import {
  getScheduleItemRegistrationState,
  type ViewerRegistrationKeys,
} from "../../../../_logic/utils/schedule-registration-match";

interface CarouselViewProps {
  dateGroups: { date: string | null; items: ScheduleItemRow[] }[];
  labels: ScheduleLabelRow[];
  /** Pre-computed label lookup map (computed once in ScheduleRenderer) */
  labelMap: Map<number, ScheduleLabelRow>;
  primaryColor: string;
  onDelete?: (item: ScheduleItemRow) => void;
  /** When provided, items become clickable (used in public view for detail modal) */
  onItemClick?: (item: ScheduleItemRow) => void;
  /** Page-wide IANA timezone for rendering times. NULL = floating time. */
  timeZone: string | null;
  isPublic?: boolean;
  /** Viewer's confirmed registrations. `null` = no logged-in attendee. */
  viewerRegistrations: ViewerRegistrationKeys | null;
}

/** Renders schedule items in a paginated carousel with navigation between date groups */
export function CarouselView({
  dateGroups,
  labels,
  labelMap,
  primaryColor,
  onDelete,
  onItemClick,
  timeZone,
  isPublic = false,
  viewerRegistrations,
}: CarouselViewProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const activeGroup = dateGroups[activeIndex] ?? dateGroups[0];

  if (!activeGroup) return null;

  return (
    <div className="flex flex-col gap-4">
      {/* Navigation */}
      <div className="flex items-center justify-between">
        {dateGroups.length > 1 ? (
          <button
            type="button"
            onClick={() => setActiveIndex((prev) => Math.max(0, prev - 1))}
            disabled={activeIndex === 0}
            className="touch-manipulation rounded-full p-2 text-gray-400 hover:text-gray-600 disabled:opacity-30"
            aria-label="Previous date"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
        ) : (
          <div className="w-9" />
        )}

        <span className="text-sm font-medium text-gray-700">
          {activeGroup.date ? <DateWithWeekday date={activeGroup.date} /> : "Unscheduled"}
        </span>

        {dateGroups.length > 1 ? (
          <button
            type="button"
            onClick={() => setActiveIndex((prev) => Math.min(dateGroups.length - 1, prev + 1))}
            disabled={activeIndex === dateGroups.length - 1}
            className="touch-manipulation rounded-full p-2 text-gray-400 hover:text-gray-600 disabled:opacity-30"
            aria-label="Next date"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        ) : (
          <div className="w-9" />
        )}
      </div>

      {/* Items */}
      <div className="flex flex-col gap-2">
        {activeGroup.items.map((item) => {
          const borderColor = getFirstLabelColor(item, labelMap) ?? primaryColor;
          const registrationState = getScheduleItemRegistrationState(item, viewerRegistrations);
          const registrationRing =
            registrationState === "registered"
              ? "ring-1 ring-emerald-500/40"
              : registrationState === "waitlisted"
                ? "ring-1 ring-amber-500/40"
                : "";
          return (
            <div
              key={item.id}
              className={`group/item overflow-hidden rounded-lg border border-gray-200 bg-white ${onItemClick ? "cursor-pointer touch-manipulation transition-shadow hover:shadow-md" : ""} ${registrationRing}`}
              onClick={onItemClick ? () => onItemClick(item) : undefined}
              role={onItemClick ? "button" : undefined}
              tabIndex={onItemClick ? 0 : undefined}
              onKeyDown={
                onItemClick
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onItemClick(item);
                      }
                    }
                  : undefined
              }
            >
              <div className="h-1 w-full" style={{ backgroundColor: borderColor }} />
              <div className="flex items-start justify-between gap-3 p-3">
                <div className="flex min-w-0 flex-col gap-1">
                  <ScheduleItemDisplay
                    item={item}
                    labels={labels}
                    showDate={activeGroup.date === null}
                    timeZone={timeZone}
                    isPublic={isPublic}
                    viewerRegistrations={viewerRegistrations}
                  />
                </div>
                {onDelete && <ItemDeleteButton item={item} onDelete={onDelete} />}
              </div>
            </div>
          );
        })}
      </div>

      {/* Dot indicators */}
      {dateGroups.length > 1 && (
        <div className="flex items-center justify-center gap-1.5">
          {dateGroups.map((group, i) => (
            <button
              key={group.date ?? "no-date"}
              type="button"
              onClick={() => setActiveIndex(i)}
              className="flex h-6 w-6 touch-manipulation items-center justify-center rounded-full transition-colors"
              aria-label={`Go to ${group.date ? formatDateShort(group.date) : "unscheduled"}`}
            >
              <span
                className={`block h-2 w-2 rounded-full transition-colors ${i === activeIndex ? "" : "bg-gray-300"}`}
                style={i === activeIndex ? { backgroundColor: primaryColor } : undefined}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
